<script>
import { computed, defineComponent, h, onBeforeUnmount, onBeforeUpdate, onMounted, ref, toRefs, watch } from 'vue';

export default defineComponent({
    name: 'Tabs',
    props: {
        defaultIndex: {
            default: 0,
            type: Number,
        },
        resetTabs: {
            type: Boolean,
            default: false,
        },
        direction: {
            type: String,
            default: 'horizontal',
            validator(value) {
                return ['horizontal', 'vertical'].includes(value);
            },
        },
        position: {
            type: String,
            default: 'left',
            validator(value) {
                return ['left', 'start', 'end', 'center'].includes(value);
            },
        },
        reverse: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: {
        tabChanged(index) {
            return index !== undefined || index !== null;
        },
    },
    setup(props, { emit, slots, attrs }) {
        const { defaultIndex, resetTabs, position, direction, reverse } = toRefs(props);
        const selectedIndex = ref(0);
        const tabs = ref([]);
        const _tabItems = ref([]);
        const onTabKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (parseInt(e.key) - 1 in tabs.value) {
                    e.preventDefault();
                    switchTab(e, parseInt(e.key) - 1, tabs.value[parseInt(e.key) - 1]);
                }
            }
        };
        const reset = () => {
            selectedIndex.value = 0;
        };
        const switchTab = (_, index, isDisabled) => {
            if (!isDisabled) {
                selectedIndex.value = index;
                emit('tabChanged', index);
            }
        };
        onMounted(() => {
            getTabItems();
            document.addEventListener('keydown', onTabKeyDown);
        });
        onBeforeUnmount(() => {
            document.removeEventListener('keydown', onTabKeyDown);
        });
        watch(
            defaultIndex,
            (newValue, oldValue) => {
                if (newValue !== selectedIndex.value) {
                    selectedIndex.value = newValue;
                }
            },
            { immediate: true },
        );
        watch(resetTabs, (newValue, oldValue) => {
            if (newValue === true) reset();
        });
        onBeforeUpdate(() => {
            getTabItems();
        });
        const getTabItems = () => {
            _tabItems.value.splice(0, _tabItems.value.length);
            slots.default().forEach((component) => {
                if (component.type.name && component.type.name === 'Tab') {
                    _tabItems.value.push(component);
                } else {
                    component.children.forEach((cComp) => {
                        if (cComp.type.name && cComp.type.name === 'Tab') {
                            _tabItems.value.push(cComp);
                        }
                    });
                }
            });
        };
        const getTitleSlotContent = (titleSlot) => {
            let slotContent = null;
            let shouldSkip = false;
            slots.default().forEach((item) => {
                if (shouldSkip) {
                    return;
                }
                if (item.type === 'template' && item.props.name === titleSlot) {
                    slotContent = item.children;
                    shouldSkip = true;
                } else {
                    if (item.children.length) {
                        item.children.forEach((cItem) => {
                            if (shouldSkip) {
                                return;
                            }
                            if (cItem.props.name === titleSlot) {
                                slotContent = cItem.children;
                                shouldSkip = true;
                            }
                        });
                    }
                }
            });
            return slotContent === null ? [] : slotContent;
        };
        const tabToDisplay = computed(() => {
            return _tabItems.value.map((item, idx) => {
                return h(
                    'div',
                    {
                        class: 'tab',
                        style: `display: ${selectedIndex.value == idx ? 'block' : 'none'}`,
                    },
                    item,
                );
            });
            // return h("div", { class: "tab" }, _tabItems.value[selectedIndex.value]);
        });
        return () => {
            const tabList = [];
            _tabItems.value.forEach((tab, index) => {
                const _tabProps = tab.props;
                const titleContent = _tabProps['title-slot']
                    ? getTitleSlotContent(_tabProps['title-slot'])
                    : _tabProps.title;
                const isDisabled = _tabProps.disabled === true || _tabProps.disabled === '';
                tabs.value[index] = isDisabled;
                tabList.push(
                    h(
                        'li',
                        {
                            class:
                                selectedIndex.value === index
                                    ? 'bg-indigo-100 text-indigo-700 px-3 py-2 font-medium text-sm rounded-md cursor-pointer'
                                    : 'text-gray-500 hover:text-gray-700 px-3 py-2 font-medium text-sm rounded-md cursor-pointer',
                            tabIndex: '0',
                            role: 'tabItem',
                            'aria-selected': selectedIndex.value === index ? 'true' : 'false',
                            'aria-disabled': isDisabled ? 'true' : 'false',
                            onClick: (e) => {
                                switchTab(e, index, isDisabled);
                            },
                        },
                        titleContent,
                    ),
                );
            });
            return h(
                'div',
                {
                    class: `tabs ${direction.value} ${reverse.value ? 'reverse' : ''}`,
                    role: 'tabs',
                },
                [
                    h('ul', { class: `flex flex-wrap space-x-4 ${position.value}`, role: 'tabList' }, tabList),
                    ...tabToDisplay.value,
                ],
            );
        };
    },
});
</script>
